import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { primaryDrawingColor, brushDrawingColor } from '../../../../assets/colors'

export type BrushDrawingState = {
    points: Point[],
}

export type BrushDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue
}

class BrushDrawingTool extends DrawingBase<any, BrushDrawingSettings, BrushDrawingState> {
    temporaryPoints: Point[] = []
    lastAddedPoint?: Point = null;
    maxTime: Time = null;
    minTime: Time = null;
    minPrice: number = null;
    maxPrice: number = null;
    path: Path2D = null;
    ctx: CanvasRenderingContext2D = null;

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        const { points } = this._state

        if (!points || points.length < 2) return;

        let currentPoint = points[0];

        const xCoordinate = this.timeToCoordinate(currentPoint.x);
        const yCoordinate = this.priceToCoordinate(currentPoint.y);

        if (!xCoordinate || !yCoordinate) return;
        let previousScaledX = scaledCoordinate(xCoordinate, window.devicePixelRatio);
        let previousScaledY = scaledCoordinate(yCoordinate, window.devicePixelRatio);
        ctx.beginPath();
        const path = new Path2D();
        path.moveTo(previousScaledX, previousScaledY);
        let i = 1;
        while (true) {
            const currentPoint = points[i];
            if (!currentPoint) break;
            const xCoordinate = this.timeToCoordinate(currentPoint.x);
            const yCoordinate = this.priceToCoordinate(currentPoint.y);

            if (!xCoordinate || !yCoordinate) break;
            const currentScaledX = scaledCoordinate(xCoordinate, scope.horizontalPixelRatio);
            const currentScaledY = scaledCoordinate(yCoordinate, scope.verticalPixelRatio);

            const nextPoint = points[i + 1];
            if (!nextPoint) {
                path.lineTo(currentScaledX, currentScaledY);
                break;
            }

            const nextXCordinate = this.timeToCoordinate(nextPoint.x);
            const nextYCordinate = this.priceToCoordinate(nextPoint.y);
            if (!nextXCordinate || !nextYCordinate) {
                break;
            }

            const nextScaledX = scaledCoordinate(nextXCordinate, scope.horizontalPixelRatio);
            const nextScaledY = scaledCoordinate(nextYCordinate, scope.verticalPixelRatio);

            if (!nextScaledX || !nextScaledY) break;


            const p1x = (previousScaledX + currentScaledX) / 2;
            const p1y = (previousScaledY + currentScaledY) / 2;
            const p2y = (currentScaledY + nextScaledY) / 2;
            const p2x = (currentScaledX + nextScaledX) / 2;
            path.lineTo(p1x, p1y);
            path.bezierCurveTo(
                p1x + (currentScaledX - p1x)*0.5,
                p1y + (currentScaledY - p1y)*0.5,
                currentScaledX + (p2x - currentScaledX)*0.5,
                currentScaledY + (p2y - currentScaledY)*0.5,
                p2x, p2y,
            )

            previousScaledX = currentScaledX;
            previousScaledY = currentScaledY;
            i++;

        }

        ctx.strokeStyle = this._settings.strokeColor.color;
        ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio;
        ctx.globalAlpha = this._settings.strokeColor.opacity;

        ctx.stroke(path);
        ctx.fillStyle = this._settings.fillColor.color;
        ctx.globalAlpha = this._settings.fillColor.opacity;
        ctx.fill(path, 'nonzero');
        ctx.closePath();
        ctx.globalAlpha = 1;
        this.path = path;
        this.ctx = ctx;
    };


    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        if (!this.minTime || time < this.minTime) this.minTime = time;
        if (!this.maxTime || time > this.maxTime) this.maxTime = time;
        if (!this.minPrice || price < this.minPrice) this.minPrice = price;
        if (!this.maxPrice || price > this.maxPrice) this.maxPrice = price;
        const statePoints = this._state.points || [];
        let pointNumber = statePoints.length;
        const point: Point = {
            id: `p${pointNumber + 1}`,
            x: time,
            y: price,
            hidden: false
        }
        const lastPoint = statePoints[statePoints.length - 1];
        if (statePoints.length < 2) {
            statePoints.push(point);
            this.lastAddedPoint = {...point};
        } else {
            const lastY = scaledCoordinate(this.priceToCoordinate(this.lastAddedPoint.y), window.devicePixelRatio);
            if (this.lastAddedPoint.x === point.x) {
                const currentY = scaledCoordinate(coordinates.y, window.devicePixelRatio);
                if (lastY && Math.abs(lastY - currentY) > 10*window.devicePixelRatio) {
                    lastPoint.hidden = true;
                    statePoints.push(point);
                    this.lastAddedPoint = {...lastPoint};
                } else {
                    lastPoint.y = point.y
                }
            } else {
                lastPoint.hidden = true;
                statePoints.push(point);
                this.lastAddedPoint = {...point};
            }
        }
        if (!preview && statePoints.length > 1) {
            this.onDrawingEnd();
            lastPoint.hidden = false;
            point.hidden = false;
        }
        this._state.points = statePoints;
        if (!preview) return { ...point }
    }
    getHandles = (): Point[] => {
        return this._state.points || [];
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = x*window.devicePixelRatio;
        const scaledY = y*window.devicePixelRatio;

        if (this.ctx && this.path && this.ctx.isPointInPath(this.path, scaledX, scaledY)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        this._state.points = points;
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        if (!this.minPrice || !this.maxPrice) return [];
        return [{
            id: 'price',
            p1: this.minPrice,
            p2: this.maxPrice,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {

        if (!this.minTime || !this.maxTime) return [];
        return [{
            id: 'time',
            p1: this.minTime,
            p2: this.maxTime,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getAvailableSettings = (): DrawingSettingGroup<BrushDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: brushDrawingColor,
                        opacity: 0.5
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: brushDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: brushDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): BrushDrawingSettings {
        return {...this._settings}
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof BrushDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'brush'
    }

    _onDrawingActive() {
        super._onDrawingActive();
        document.addEventListener('mouseup', this.onMouseUp);
    }

    onMouseUp = () => {
        if(!this._state?.points?.length) return
        this.onDrawingEnd();
        const lastPoint = this._state.points[this._state.points.length - 1];
        if (lastPoint) lastPoint.hidden = false;
        document.removeEventListener('mouseup', this.onMouseUp);
    }

    destroy() {
        super.destroy();
        this.ctx = null;
        this.path = null;
    }
}

export default BrushDrawingTool